<script setup lang="ts">
    import { computed, watch, onMounted, ref, type Ref } from 'vue'
    import { EditorState, generateName } from "@common/Common"
    import { guid } from "@common/Guid"

    import
    {
        useNewScenarioSettingsQueryV1,
        useExistingScenarioSettingsQueryV1
    } from "@Quest/PortfolioScenarios/queries-vue-composables"

    import
    {
        useCreatePortfolioScenarioCommandV1,
        useUpdatePortfolioScenarioCommandV1
    } from "@Quest/PortfolioScenarios/commands-vue-composables"

    import type {
        PortfolioScenarioSettingsReadModel,
        WeightsSetReadModel
    } from "@Quest/PortfolioScenarios/readmodels"

    import type { PortfolioScenarioEditorViewModel } from "@Quest/PortfolioScenarios/Components/PortfolioScenarioEditor"
    import { CreatePortfolioScenarioCommandV1, UpdatePortfolioScenarioCommandV1 } from '../commands-core'


    // ======================
    // Props
    // ======================

    const props = defineProps<{
        viewModel: PortfolioScenarioEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'scenario-created', id: string): void
        (e: 'scenario-updated', id: string): void
    }>()

    const vm = props.viewModel

    // ======================
    // Derived props
    // ======================

    const opened = computed(() => vm?.opened ?? false)

    const workspaceId = computed(() => vm.workspaceId)
    const portfolioId = computed(() => vm.portfolioId)
    const portfolioScenarioId = computed(() => vm.portfolioScenarioId)

    const corporateScenarioId = computed({
        get: () => vm.corporateScenarioId,
        set: (val: string) => vm.corporateScenarioId = val
    })

    // ======================
    // Queries
    // ======================

    const {
        runNewScenarioSettingsQueryV1,
        modelNewScenarioSettingsQueryV1
    } = useNewScenarioSettingsQueryV1(workspaceId, portfolioId)

    const {
        runExistingScenarioSettingsQueryV1,
        modelExistingScenarioSettingsQueryV1
    } = useExistingScenarioSettingsQueryV1(workspaceId, portfolioId)

    const rm = computed<PortfolioScenarioSettingsReadModel | null>(() =>
    {
        if (vm.state === EditorState.Creator)
        {
            return (modelNewScenarioSettingsQueryV1 as Ref<PortfolioScenarioSettingsReadModel | null>).value
        }

        if (vm.state === EditorState.Editor || vm.state === EditorState.Cloner)
        {
            return (modelExistingScenarioSettingsQueryV1 as Ref<PortfolioScenarioSettingsReadModel | null>).value
        }

        return null
    })

    // ======================
    // Local State
    // ======================

    const criteriaHierarchyId = ref<number | null>(null)
    const weightsSetId = ref<number | null>(null)

    const weightsSets = computed<WeightsSetReadModel[]>(() =>
    {
        return rm.value?.ValueDefinitions
            .find(a => a.Id === criteriaHierarchyId.value)
            ?.WeightsSets ?? []
    })

    // ======================
    // Commands
    // ======================

    const {
        invokeCreatePortfolioScenarioCommandV1,
        onSucceedCreatePortfolioScenarioCommandV1,
        command: createPortfolioScenarioCommandV1
    } = useCreatePortfolioScenarioCommandV1(workspaceId, corporateScenarioId, portfolioId)

    const {
        invokeUpdatePortfolioScenarioCommandV1,
        onSucceedUpdatePortfolioScenarioCommandV1,
        command: updatePortfolioScenarioCommandV1
    } = useUpdatePortfolioScenarioCommandV1(workspaceId, corporateScenarioId, portfolioId)

    // ======================
    // Watchers
    // ======================

    // Opened watcher (replaces onOpenedChanged)
    watch(opened, (val) =>
    {
        if (val === true)
        {
            if (vm.state === EditorState.Creator)
            {
                runNewScenarioSettingsQueryV1({ CorporateScenarioId: corporateScenarioId.value })
            }
            else if (vm.state === EditorState.Editor || vm.state === EditorState.Cloner)
            {
                runExistingScenarioSettingsQueryV1({ PortfolioScenarioId: portfolioScenarioId.value })
            }
        }
    })

    // Criteria hierarchy watcher (replaces onCriteriaHierarchyIdChanged)
    watch(criteriaHierarchyId, () =>
    {
        if (!weightsSetId.value) return

        if (!weightsSets.value.some(a => a.Id === weightsSetId.value))
        {
            weightsSetId.value = null
        }
    })

    watch(rm, (current) =>
    {
        if (!current) return

        if (vm.state === EditorState.Cloner)
        {
            current.Name = generateName(current.Name)
        }

        criteriaHierarchyId.value = current.CriteriaHierarchyId
        weightsSetId.value = current.WeightsSetId
        corporateScenarioId.value = current.CorporateScenarioId
    }, { immediate: true })

    onSucceedUpdatePortfolioScenarioCommandV1(() =>
    {
        const cmd = updatePortfolioScenarioCommandV1.value as UpdatePortfolioScenarioCommandV1

        emit('scenario-updated', cmd.Id)
        close()
    })

    onSucceedCreatePortfolioScenarioCommandV1(() =>
    {
        const cmd = createPortfolioScenarioCommandV1.value as CreatePortfolioScenarioCommandV1

        emit('scenario-created', cmd.Id)
        close()
    })

    // ======================
    // Methods
    // ======================

    function onFormSubmit()
    {
        if (!rm.value) return

        if (vm.state === EditorState.Creator)
        {
            invokeCreatePortfolioScenarioCommandV1({
                Id: guid(),
                Name: rm.value.Name,
                Description: rm.value.Description,
                CriteriaHierarchyId: criteriaHierarchyId.value,
                WeightsSetId: weightsSetId.value,
                ApplyBenefitsConfidenceFactor: rm.value.ApplyBenefitsConfidenceFactor,
                Denomination: rm.value.Denomination,
                ApprovalAcceptedStatusId: rm.value.ApprovalAcceptedStatusId,
                ApprovalRejectedStatusId: rm.value.ApprovalRejectedStatusId
            })
        }
        else if (vm.state === EditorState.Editor)
        {
            invokeUpdatePortfolioScenarioCommandV1({
                Id: rm.value.Id,
                Name: rm.value.Name,
                Description: rm.value.Description,
                CriteriaHierarchyId: criteriaHierarchyId.value,
                WeightsSetId: weightsSetId.value,
                ApplyBenefitsConfidenceFactor: rm.value.ApplyBenefitsConfidenceFactor,
                Denomination: rm.value.Denomination,
                ApprovalAcceptedStatusId: rm.value.ApprovalAcceptedStatusId,
                ApprovalRejectedStatusId: rm.value.ApprovalRejectedStatusId
            })
        }
    }

    function close()
    {
        vm.close()
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ vm.modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <form v-if="rm" @submit.prevent="onFormSubmit" id="settingsForm">
                            <div class="form-row">
                                <div class="form-group col">
                                    <label>Plan name:</label>
                                    <input type="text" v-model.trim="rm.Name" class="form-control" required minlength="2" />
                                </div>
                                <div class="form-group col">
                                    <label>Link to Enterprise Plan:</label>
                                    <select class="form-control" v-model="corporateScenarioId" :disabled="rm.IsActive">
                                        <option :value="null" disabled>Choose...</option>
                                        <option v-for="cs in rm.CorporateScenarios" :value="cs.Id">
                                            {{ cs.Name }}
                                        </option>
                                    </select>
                                    <small class="form-text text-muted" v-if="rm.IsActive">
                                        This cannot be changed because the plan is currently active in that enterprise plan.
                                    </small>
                                </div>

                            </div>

                            <div class="form-group">
                                <label>Description:</label>
                                <textarea v-model.trim="rm.Description" class="form-control" />
                            </div>

                            <div class="form-row">
                                <div class="form-group col">
                                    <label for="denomination">Currency denomination:</label>
                                    <select id="denomination" v-model="rm.Denomination" class="form-control">
                                        <option value="Unit">Unit</option>
                                        <option value="Thousand">Thousand</option>
                                        <option value="Million">Million</option>
                                    </select>
                                </div>

                                <div class="form-group col">
                                    <label>Risk adjusted benefits:</label>
                                    <select class="form-control" v-model="rm.ApplyBenefitsConfidenceFactor">
                                        <option :value="true">Enabled</option>
                                        <option :value="false">Disabled</option>
                                    </select>
                                </div>
                            </div>


                            <fieldset>
                                <legend>Value definition</legend>

                                <div class="form-group">
                                    <label>Goal:</label>
                                    <select class="form-control" v-model="criteriaHierarchyId">
                                        <option :value="null">Choose...</option>
                                        <option v-for="ch in rm.ValueDefinitions" :value="ch.Id">{{ch.Name}}</option>
                                    </select>
                                </div>
                                <div class="form-group">
                                    <label>Weights set:</label>
                                    <select class="form-control" v-model="weightsSetId">
                                        <option :value="null">Default</option>
                                        <option v-for="ws in weightsSets" :value="ws.Id">{{ws.Name}}</option>
                                    </select>
                                </div>
                            </fieldset>


                            <fieldset>
                                <legend>Approval</legend>

                                <div class="form-group">
                                    <label>Accepted:</label>
                                    <select class="form-control" v-model="rm.ApprovalAcceptedStatusId">
                                        <option :value="null">Do not change</option>
                                        <option v-for="s in rm.Statuses" :value="s.Id">{{s.Name}}</option>
                                    </select>
                                    <small class="form-text text-muted">
                                        Kanban status for Accepted projects. The Kanban status will be updated when you Approve your decision in the scenario.
                                    </small>
                                </div>
                                <div class="form-group">
                                    <label>Rejected:</label>
                                    <select class="form-control" v-model="rm.ApprovalRejectedStatusId">
                                        <option :value="null">Do not change</option>
                                        <option v-for="s in rm.Statuses" :value="s.Id">{{s.Name}}</option>
                                    </select>
                                    <small class="form-text text-muted">
                                        Kanban status for Rejected projects. The Kanban status will be updated when you Approve your decision in the scenario.
                                    </small>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="settingsForm" class="btn btn-primary">Confirm</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
